/**
 * Resolve Stored Placement
 *
 * The one read-time rule for a persisted DesignPosition row: its stored
 * `placementMm` is the design's box in millimetres on the row's OWN print
 * area (R17, R36). Every row carries its own placement -- the master row,
 * every derived row (rewritten by the carry-over whenever the master
 * position is written) and every custom row -- so a read applies no
 * carry-over rule and needs neither the design's master nor a recorded
 * source area. Product cards, the print files, the position export and the
 * agent tools all read through here, so a row means the same thing
 * everywhere.
 *
 * The relative view a reader gets is computed from the placement and the
 * area's current millimetres (`placementMmToRelative`). When an admin
 * changes an area's millimetres after the save, the design keeps its size
 * in mm and its relative width follows (AE17); derived rows are carried
 * over again on the next write of the master position.
 *
 * A row without a usable `placementMm` has no placement: readers skip it.
 */

import type { RelativeDesignState } from '../design/placementTypes.js';
import type { ProductClass } from '../design/carryOverDefaults.js';
import type { PrintContext } from '../print/context.js';
import { isFinitePositive } from '../print/templateGeometry.js';
import {
  isPlacementMm,
  placementMmToRelative,
  type PlacementMm,
} from './placementMm.js';
import {
  contextWidthMm,
  sourceTierFromContext,
  type PlacementArea,
  type SourceTierContext,
} from './sizeTier.js';

/** The persisted field the rule reads; the JSON column is untyped. */
export interface StoredPlacementRow {
  placementMm?: unknown;
}

/** The millimetres of the area a row is read on. */
export type StoredPlacementArea = Pick<
  PlacementArea,
  'maxWidthMm' | 'maxHeightMm'
>;

/**
 * Live lookup of a product's class by id. Returns `undefined` when the
 * product is unknown to the reader, `null` when the product exists and has
 * no class.
 */
export type ProductClassLookup = (
  productId: string
) => ProductClass | null | undefined;

/** Guard for an untyped relative state (in-memory and API payload values). */
export function isRelativeDesignState(
  value: unknown
): value is RelativeDesignState {
  if (value == null || typeof value !== 'object') return false;
  const rel = value as Record<string, unknown>;
  return (
    typeof rel.relCenterX === 'number' &&
    typeof rel.relCenterY === 'number' &&
    typeof rel.relWidth === 'number' &&
    typeof rel.designRatio === 'number'
  );
}

/** The stored placement of a row, or null when the row has none. */
export function storedPlacementMm(row: StoredPlacementRow): PlacementMm | null {
  return isPlacementMm(row.placementMm) ? row.placementMm : null;
}

/**
 * The relative state of a stored row on its own print area, or `null` when
 * the row has no placement or the area has no millimetres (a position
 * without millimetres is not configured, R31).
 */
export function resolveStoredPlacement(
  row: StoredPlacementRow,
  area: StoredPlacementArea
): RelativeDesignState | null {
  const placement = storedPlacementMm(row);
  if (!placement) return null;
  if (!isFinitePositive(area.maxWidthMm) || !isFinitePositive(area.maxHeightMm))
    return null;
  return placementMmToRelative(placement, {
    widthMm: area.maxWidthMm,
    heightMm: area.maxHeightMm,
  });
}

// =============================================================================
// IN-MEMORY EDITOR CONTEXT (web, until U13)
// =============================================================================

/**
 * The subset of an in-memory print context the placement area is read from.
 *
 * @deprecated The web editor still works on canvas-pixel print contexts;
 * U13 moves it to millimetre areas (`placementAreaFromMm`) and removes this.
 */
export type PrintContextLike = Pick<PrintContext, 'widthPx' | 'heightPx'> &
  Partial<
    Pick<
      PrintContext,
      | 'widthMm'
      | 'pxPerMm'
      | 'sizeTier'
      | 'sizeTierOverride'
      | 'productId'
      | 'productClass'
      | 'carryOverPercent'
      | 'carryOverAnchor'
      | 'carryOverTopOffsetMm'
    >
  >;

/**
 * The placement area of an in-memory print context (editor, switch anchor,
 * guided entry), with the px-as-mm guard and the context's carry-over
 * settings.
 *
 * @deprecated Web-only until U13 (see `PrintContextLike`).
 */
export function areaFromPrintContext(ctx: PrintContextLike): PlacementArea {
  const area: PlacementArea = { width: ctx.widthPx, height: ctx.heightPx };
  const tierCtx: SourceTierContext = {
    sizeTier: ctx.sizeTier,
    widthMm: ctx.widthMm,
    printWidthPx: ctx.widthPx,
    pxPerMm: ctx.pxPerMm,
  };
  const widthMm = contextWidthMm(tierCtx);
  if (widthMm !== null) area.maxWidthMm = widthMm;
  const sizeTier = sourceTierFromContext(tierCtx);
  if (sizeTier !== undefined) area.sizeTier = sizeTier;
  if (ctx.sizeTierOverride) area.sizeTierOverride = ctx.sizeTierOverride;
  if (ctx.productId) area.productId = ctx.productId;
  if (ctx.productClass !== undefined) area.productClass = ctx.productClass;
  if (ctx.carryOverPercent != null) {
    area.carryOverPercent = ctx.carryOverPercent;
  }
  if (ctx.carryOverAnchor != null) area.carryOverAnchor = ctx.carryOverAnchor;
  if (ctx.carryOverTopOffsetMm != null) {
    area.carryOverTopOffsetMm = ctx.carryOverTopOffsetMm;
  }
  return area;
}
